import { useInitialLoading } from '../hooks/useInitialLoading';
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { getAuthHeaders } from '../utils/authHeaders';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

const PositionLevelList = () => {
    const companyId = localStorage.getItem('selectedCompanyId');
    const authConfig = () => ({ headers: getAuthHeaders() });
    const [levels, setLevels] = useState([]);
    const [loading, setLoading] = useState(true);
  const initialLoading = useInitialLoading(loading);
    const [error, setError] = useState(null);

    // Form state
    const [name, setName] = useState('');
    const [rankOrder, setRankOrder] = useState(1);
    const [description, setDescription] = useState('');
    const [status, setStatus] = useState('Active');

    const fetchLevels = async () => {
        try {
            const response = await axios.get(`${API_BASE}/companies/${companyId}/position-levels`, authConfig());
            setLevels(response.data.data);
        } catch (err) {
            setError('Failed to fetch position levels.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchLevels();
    }, [companyId]);

    const handleCreate = async (e) => {
        e.preventDefault();
        try {
            await axios.post(`${API_BASE}/companies/${companyId}/position-levels`, {
                name,
                rank_order: rankOrder,
                description,
                status
            }, authConfig());
            setName('');
            setRankOrder(1);
            setDescription('');
            fetchLevels();
            alert('Position level created successfully!');
        } catch (err) {
            alert('Error creating position level.');
        }
    };

    if (initialLoading) return <div className="p-6 text-center text-gray-600">Loading position levels...</div>;
    if (error) return <div className="p-6 text-center text-red-600">{error}</div>;

    return (
        <div className="max-w-6xl mx-auto p-6 mt-10">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Position Ranks & Levels</h2>

            {/* Creation Form */}
            <form onSubmit={handleCreate} className="bg-white p-6 rounded-lg shadow-md mb-8 grid grid-cols-1 md:grid-cols-5 gap-4 items-end">
                <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Level Name</label>
                    <input 
                        type="text" 
                        value={name} 
                        onChange={(e) => setName(e.target.value)} 
                        placeholder="e.g. Senior" 
                        required 
                        className="w-full border border-gray-300 rounded-md p-2 text-sm"
                    />
                </div>
                <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Rank Order</label>
                    <input 
                        type="number" 
                        value={rankOrder} 
                        onChange={(e) => setRankOrder(e.target.value)} 
                        required 
                        className="w-full border border-gray-300 rounded-md p-2 text-sm"
                    />
                </div>
                <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Description</label>
                    <input 
                        type="text" 
                        value={description} 
                        onChange={(e) => setDescription(e.target.value)} 
                        placeholder="Optional details" 
                        className="w-full border border-gray-300 rounded-md p-2 text-sm"
                    />
                </div>
                <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Status</label>
                    <select 
                        value={status} 
                        onChange={(e) => setStatus(e.target.value)} 
                        className="w-full border border-gray-300 rounded-md p-2 text-sm bg-white"
                    >
                        <option value="Active">Active</option>
                        <option value="Inactive">Inactive</option>
                    </select>
                </div>
                <div>
                    <button type="submit" className="w-full bg-blue-600 text-white py-2 rounded-md hover:bg-blue-700 text-sm font-medium">
                        + Add Level
                    </button>
                </div>
            </form>

            {/* Levels Table */}
            <div className="bg-white shadow-md rounded-lg overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rank Order</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Level Name</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {levels.length === 0 ? (
                            <tr>
                                <td colSpan="4" className="px-6 py-4 text-center text-gray-500">No position levels found. Create your first one above!</td>
                            </tr>
                        ) : (
                            levels.map((lvl) => (
                                <tr key={lvl.id} className="hover:bg-gray-50">
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{lvl.rank_order}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{lvl.name}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{lvl.description || 'N/A'}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                            lvl.status === 'Active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                                        }`}>
                                            {lvl.status}
                                        </span>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default PositionLevelList;